/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * What the seat steppers are asking for, and what it costs.
 *
 * Adding is charged today, pro rata to the days left in the cycle. Reducing
 * is not refunded and does not take effect until the renewal, so the two
 * halves of a mixed change are quoted separately — one is a charge now, the
 * other is a smaller bill later, and reading them as one number would say
 * neither.
 */

import type { SubscriptionLine } from './seat-reduction';

/** How many seats of each plan the steppers currently show. */
export type SeatDraft = Record<string, number>;

/** What one plan costs per seat, from the catalogue. */
export interface SeatPrice {
  planId: string;
  centsPerSeat: number;
}

/** The draft a block opens on: exactly what is contracted today. */
export const draftFrom = (items: SubscriptionLine[]): SeatDraft => (
  Object.fromEntries((items || []).map((line) => [line.plan_id, line.quantity]))
);

/**
 * The floor a plan's stepper cannot go below.
 *
 * The plan the owner sits on keeps at least one seat: an agency cannot give
 * up the seat of the person who owns it, and a stepper that allowed zero
 * would be offering a subscription with nobody in it.
 */
export const floorFor = (planId: string, ownerPlanId: string | null): number => (
  planId === ownerPlanId ? 1 : 0
);

/** Move one plan's counter, honouring its floor and the catalogue ceiling. */
export const step = (
  draft: SeatDraft,
  planId: string,
  by: number,
  ownerPlanId: string | null,
  maxSeats: number | null = null,
): SeatDraft => {
  const floor = floorFor(planId, ownerPlanId);
  const wanted = (draft[planId] ?? 0) + by;
  const ceiling = maxSeats == null ? wanted : Math.min(wanted, maxSeats);
  return { ...draft, [planId]: Math.max(floor, ceiling) };
};

/** How each plan's draft differs from what is contracted. */
export const deltas = (
  items: SubscriptionLine[], draft: SeatDraft,
): Record<string, number> => {
  const now = draftFrom(items);
  const planIds = new Set([...Object.keys(now), ...Object.keys(draft)]);
  const out: Record<string, number> = {};
  planIds.forEach((planId) => {
    const difference = (draft[planId] ?? 0) - (now[planId] ?? 0);
    if (difference !== 0) out[planId] = difference;
  });
  return out;
};

/** Whether the steppers are asking for anything at all. */
export const isDirty = (items: SubscriptionLine[], draft: SeatDraft): boolean => (
  Object.keys(deltas(items, draft)).length > 0
);

/**
 * Price of.
 */
const priceOf = (prices: SeatPrice[], planId: string): number => (
  prices.find((price) => price.planId === planId)?.centsPerSeat ?? 0
);

/**
 * What the seats being added cost today.
 *
 * Pro rata to the days left: the agency is buying part of a cycle it has
 * already paid for, and charging the full seat price for nine days of it
 * would be charging for time it does not get.
 *
 * Rounded once, at the end. Rounding each line first and adding the results
 * drifts by a cent per plan, which is the kind of difference somebody
 * eventually notices on an invoice.
 */
export const proRataCents = (
  items: SubscriptionLine[],
  draft: SeatDraft,
  prices: SeatPrice[],
  daysRemaining: number,
  cycleDays: number,
): number => {
  if (cycleDays <= 0) return 0;
  const added = Object.entries(deltas(items, draft)).filter(([, by]) => by > 0);
  const full = added.reduce(
    (total, [planId, by]) => total + priceOf(prices, planId) * by, 0,
  );
  // Clamped to the cycle, as the backend charge is: a monthly cycle advanced
  // by calendar months can leave 31 days against 30, and the unclamped ratio
  // quoted 103% of a price the server would never actually charge.
  const days = Math.min(Math.max(0, daysRemaining), cycleDays);
  return Math.round((full * days) / cycleDays);
};

/** What the subscription will cost at the next renewal, as drafted. */
export const renewalTotalCents = (
  draft: SeatDraft, prices: SeatPrice[],
): number => Object.entries(draft).reduce(
  (total, [planId, seats]) => total + priceOf(prices, planId) * Math.max(0, seats), 0,
);

/** Seats across every plan, as drafted. */
export const totalSeats = (draft: SeatDraft): number => (
  Object.values(draft).reduce((sum, seats) => sum + Math.max(0, seats), 0)
);

/**
 * Whether this draft may be confirmed at all.
 *
 * The subscription floor is the product's, not any one plan's: one seat on
 * one plan beside four on another is five seats and a team it sells.
 */
export const meetsFloor = (draft: SeatDraft, floor: number): boolean => (
  totalSeats(draft) >= floor
);

/** Days between now and the renewal, never negative. */
export const daysUntil = (isoDate: string | null | undefined, now: Date): number => {
  if (!isoDate) return 0;
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.ceil((then - now.getTime()) / 86400000));
};

/** The lines a draft amounts to, as the change-items route reads them. */
export const asItems = (draft: SeatDraft): Array<{ plan_id: string; seats: number }> => (
  Object.entries(draft)
    .filter(([, seats]) => seats > 0)
    .map(([plan_id, seats]) => ({ plan_id, seats }))
);
